import { Stack, Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import { t } from "i18next";
import { useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Divider,
  NumberFieldFormikField,
  Radio,
  RadioGroupFormikField,
  SelectFormikField,
  SelectItem,
  TextField,
  TextInputFormikField,
  Toggle,
} from "@certn/cascadia-react";
import { Trans } from "react-i18next";
import { ReferenceRuleOptionSelect } from "../../experience-verification-templates/ReferenceRuleOptionSelect";
import { ExperienceVerificationTemplateFormField } from "../../experience-verification-templates/ExperienceVerificationTemplateForm";
import {
  EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG,
  GAP_VERIFICATION_OPTIONS,
} from "../../experience-verification-templates/utils";
import { FormikProps } from "formik";
import { VerificationTemplate } from "../create/VerificationTemplatesContext";
import {
  EMPLOYMENT_GAP_PERIOD_MONTHS_OPTIONS,
  EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG,
  GAP_THRESHOLD_UNIT_OPTIONS,
} from "../utils";
import { useShouldShowRegulatoryReferences } from "../hooks";
import { FCARegulatedReferences } from "./FCARegulatedReferences";

const getDocumentCollectionModeLabel = (mode: string): string => {
  const labels: Record<string, string> = {
    MOST_RECENT: t("Most Recent"),
    START_AND_MOST_RECENT: t("Start and Most Recent"),
    FULL_DURATION: t("Full Duration"),
  };
  return labels[mode] ?? mode;
};

interface Props {
  checkArguments?: VerificationTemplate["check_arguments"];
  isReadOnly?: boolean;
  isQuantity?: boolean;
  setValues?: FormikProps<VerificationTemplate>["setValues"];
}

export function VerificationDetails({
  checkArguments,
  isQuantity,
  isReadOnly,
  setValues,
}: Props) {
  const {
    caseManagementEnableEmploymentVerificationGapPeriod,
    releaseGapConfigForEmploymentVerification,
    enableGapDetectionInDaysForEmpVerification,
    enableFcaRegulatedReferences,
    caseManagementEmploymentSuperunitFirst,
  } = useFlags();

  const [enableDocumentCollection, setEnableDocumentCollection] = useState(
    !!checkArguments?.document_collection_mode
  );
  const shouldShowQuantityFcaSection = useShouldShowRegulatoryReferences(
    !!enableFcaRegulatedReferences && !!isQuantity
  );

  const handleDocumentCollectionToggle = (selected: boolean) => {
    setEnableDocumentCollection(selected);
    if (!selected) {
      setValues?.((prev) => ({
        ...prev,
        check_arguments: {
          ...prev.check_arguments,
          document_collection_mode: undefined,
        },
      }));
    } else {
      setValues?.((prev) => ({
        ...prev,
        check_arguments: {
          ...prev.check_arguments,
          document_collection_mode: "MOST_RECENT",
        },
      }));
    }
  };

  const handleGapVerificationChange = (value: string) => {
    // Rules can only be set when the type is REFERENCE
    if (value === "STATEMENT") {
      setValues?.((prev) => ({
        ...prev,
        check_arguments: {
          ...prev.check_arguments,
          gap_verification_type: "STATEMENT",
          reference_rule_options: undefined,
        },
      }));
    }

    // Set the default for when the type is REFERENCE
    if (value === "REFERENCE") {
      setValues?.((prev) => ({
        ...prev,
        check_arguments: {
          ...prev.check_arguments,
          gap_verification_type: "REFERENCE",
          reference_rule_options: "DEFAULT",
        },
      }));
    }
  };

  const handleGapThresholdUnitChange = (unit: string) => {
    if (!setValues) {
      return;
    }
    if (unit === "MONTHS") {
      setValues((prev) => ({
        ...prev,
        check_arguments: {
          ...prev.check_arguments,
          gap_threshold_unit: "MONTHS",
          minimum_gap_period_months:
            prev.check_arguments?.minimum_gap_period_months ??
            EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MINIMUM_GAP_PERIOD_MONTHS,
          minimum_gap_period_days: undefined,
        },
      }));
    } else {
      setValues((prev) => ({
        ...prev,
        check_arguments: {
          ...prev.check_arguments,
          gap_threshold_unit: "DAYS",
          minimum_gap_period_months: undefined,
          minimum_gap_period_days: undefined,
        },
      }));
    }
  };

  const gapThresholdUnit = checkArguments?.gap_threshold_unit ?? "MONTHS";
  const isGapThresholdMonths = gapThresholdUnit !== "DAYS";
  const includeGapDetection = checkArguments?.include_gap_detection !== false;

  return (
    <>
      <Text style="form/section-heading">
        {isQuantity ? (
          <Trans>Number of employers</Trans>
        ) : (
          <Trans>Employment years</Trans>
        )}
      </Text>
      {isQuantity ? (
        <>
          <NumberFieldFormikField
            name="check_arguments.minimum"
            testID="minimum-employers"
            isRequired={isQuantity}
            isReadOnly={isReadOnly}
            isDisabled={isReadOnly}
            label={t("Minimum number of employer(s)")}
            description={t(
              "Indicate the least amount of employers you need verified."
            )}
            minValue={
              EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_EMPLOYERS_MINIMUM
            }
            maxValue={checkArguments?.maximum}
          />
          <NumberFieldFormikField
            name="check_arguments.maximum"
            testID="maximum-employers"
            isRequired={isQuantity}
            isReadOnly={isReadOnly}
            isDisabled={isReadOnly}
            label={t("Maximum number of employer(s)")}
            description={t(
              "Indicate the maximum amount of employers you need verified."
            )}
            minValue={checkArguments?.minimum}
            maxValue={
              EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_EMPLOYERS_MAXIMUM
            }
          />
          {!!enableFcaRegulatedReferences && shouldShowQuantityFcaSection && (
            <>
              <Divider />
              <FCARegulatedReferences
                isReadOnly={!!isReadOnly}
                showYearsGuidance={false}
              />
            </>
          )}
        </>
      ) : (
        <Stack gap="space-4">
          <NumberFieldFormikField
            name="check_arguments.years"
            testID="years-employers"
            isReadOnly={isReadOnly}
            isDisabled={isReadOnly}
            isRequired={!isQuantity}
            label={t("How many years?")}
            description={t(
              "Indicate how many years of employers you need verified."
            )}
            minValue={EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_YEARS}
            maxValue={EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_YEARS}
          />
          {caseManagementEnableEmploymentVerificationGapPeriod && (
            <NumberFieldFormikField
              name="check_arguments.maximum_gap_period_in_days"
              testID="maximum-gap-period-in-days"
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
              isRequired={!isQuantity}
              label={t("Maximum gap period in days")}
              description={t(
                "Indicate the maximum amount of days the applicant can have for a gap period."
              )}
              minValue={1}
              maxValue={365}
            />
          )}
          {!!enableFcaRegulatedReferences && (
            <FCARegulatedReferences isReadOnly={!!isReadOnly} />
          )}
          {releaseGapConfigForEmploymentVerification && (
            <Stack gap="space-4">
              <Divider />
              <Stack gap="space-5">
                <ExperienceVerificationTemplateFormField
                  title={t("Gap threshold")}
                  description={t(
                    "Control whether gaps are identified and when they require verification."
                  )}
                />
                <Toggle
                  isSelected={includeGapDetection}
                  onChange={(selected) => {
                    setValues?.((prev) => ({
                      ...prev,
                      check_arguments: {
                        ...prev.check_arguments,
                        include_gap_detection: selected,
                      },
                    }));
                  }}
                  isDisabled={isReadOnly}
                  testID="include-gap-detection"
                  description={t(
                    "Ask applicants to explain gaps in their history"
                  )}
                >
                  {t("Include gap detection and verification")}
                </Toggle>
                {includeGapDetection && (
                  <>
                    <Stack gap="space-5">
                      {enableGapDetectionInDaysForEmpVerification ? (
                        <Stack gap="space-1">
                          <Text style="input/label">
                            {t("Minimum gap length")}
                          </Text>
                          <Stack
                            orientation="horizontal"
                            gap="space-3"
                            align="start"
                          >
                            <div className={narrowSelectStyle}>
                              <SelectFormikField
                                label=""
                                name="check_arguments.gap_threshold_unit"
                                isRequired
                                testID="gap-threshold-unit"
                                isDisabled={isReadOnly}
                                onSelectionChange={(value) =>
                                  handleGapThresholdUnitChange(String(value))
                                }
                              >
                                {GAP_THRESHOLD_UNIT_OPTIONS().map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    id={option.value}
                                    textValue={option.label}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectFormikField>
                            </div>
                            <TextInputFormikField
                              name={
                                isGapThresholdMonths
                                  ? "check_arguments.minimum_gap_period_months"
                                  : "check_arguments.minimum_gap_period_days"
                              }
                              label=""
                              isRequired
                              testID="minimum-gap-period-value"
                              isDisabled={isReadOnly}
                              placeholder={isGapThresholdMonths ? "6" : "1"}
                              inputMode="numeric"
                              autoComplete="off"
                              pattern="[0-9]*"
                            />
                          </Stack>
                          <Text style="form/body-secondary" color="text-weak">
                            {t(
                              "For example, if this is set to 6 months, applicants will only be asked to explain gaps lasting 6 months or more."
                            )}
                          </Text>
                        </Stack>
                      ) : (
                        <SelectFormikField
                          label={t("Minimum length of gap")}
                          name="check_arguments.minimum_gap_period_months"
                          isRequired
                          testID="minimum-gap-period-months"
                          description={t(
                            "For example, if this is set to 6 months, applicants will only be asked to explain gaps lasting 6 months or more."
                          )}
                          isDisabled={isReadOnly}
                        >
                          {EMPLOYMENT_GAP_PERIOD_MONTHS_OPTIONS({
                            hideBPSSReference: true,
                          }).map((option) => (
                            <SelectItem
                              key={option.value}
                              id={option.value}
                              textValue={option.label}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectFormikField>
                      )}
                    </Stack>
                    <Divider />
                    <Stack gap="space-5">
                      <ExperienceVerificationTemplateFormField
                        title={t("Gap detail requirements")}
                        description={t(
                          "For gaps that meet or exceed your threshold, choose the level of detail required."
                        )}
                      />
                      <RadioGroupFormikField
                        isCard
                        testID="gap-verification-type"
                        name="check_arguments.gap_verification_type"
                        orientation="vertical"
                        isRequired
                        onChange={(value) => {
                          handleGapVerificationChange(value);
                        }}
                        isDisabled={isReadOnly}
                      >
                        {GAP_VERIFICATION_OPTIONS()
                          .filter((option) => option.key !== "documentation")
                          .map((option) => (
                            <Radio
                              key={option.key}
                              testID={`gap-verification-${option.key}-type`}
                              value={option.value}
                              description={option.description}
                              options={
                                option.key === "reference" &&
                                checkArguments?.gap_verification_type ===
                                  "REFERENCE" ? (
                                  <ReferenceRuleOptionSelect
                                    name={
                                      "check_arguments.reference_rule_options"
                                    }
                                    isDisabled={isReadOnly}
                                    hideBPSSReference
                                  />
                                ) : undefined
                              }
                            >
                              {option.label}
                            </Radio>
                          ))}
                      </RadioGroupFormikField>
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
      {caseManagementEmploymentSuperunitFirst && (
        <>
          <Divider />
          <Text style="form/section-heading">
            <Trans>Document Collection</Trans>
          </Text>
          <Text style="input/helper" color="text-weak">
            {t(
              "When enabled, we will ask the candidate to provide documentation for their employment. If we are unable to successfully contact the employer, these documents will be used to verify the employment."
            )}
          </Text>
          {isReadOnly ? (
            checkArguments?.document_collection_mode && (
              <TextField
                label={t("Document collection mode")}
                value={getDocumentCollectionModeLabel(
                  checkArguments.document_collection_mode
                )}
                isDisabled
                testID="document-collection-mode-readonly"
              />
            )
          ) : (
            <>
              <Toggle
                isSelected={enableDocumentCollection}
                onChange={handleDocumentCollectionToggle}
                testID="enable-document-collection"
              >
                {t("Enable document collection")}
              </Toggle>
              {enableDocumentCollection && (
                <RadioGroupFormikField
                  isCard
                  testID="document-collection-mode"
                  name="check_arguments.document_collection_mode"
                  orientation="vertical"
                  isRequired
                >
                  <Radio
                    testID="document-collection-most-recent"
                    value="MOST_RECENT"
                    description={t(
                      "Collects the most recent pay document for the employer."
                    )}
                  >
                    {t("Most Recent")}
                  </Radio>
                  <Radio
                    testID="document-collection-start-and-most-recent"
                    value="START_AND_MOST_RECENT"
                    description={t(
                      "Collects one document from the start of employment and their most recent."
                    )}
                  >
                    {t("Start and Most Recent")}
                  </Radio>
                  <Radio
                    testID="document-collection-full-duration"
                    value="FULL_DURATION"
                    description={t(
                      "Collects one document per year for the duration of the employment."
                    )}
                  >
                    {t("Full Duration")}
                  </Radio>
                </RadioGroupFormikField>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

const narrowSelectStyle = css({ width: "140px", flexShrink: 0 });
